<template lang="pug">
.property-add
  .container-fluid.property-add-header
    .container
      .row.py-3
        .col-5
          h3.title-path
            router-link(to="/") {{ $t('property_list_page.property_list') }} ({{ $t('property_list_page.top') }})
            span#arrow-delimiter >
            span {{ title }}
          h1 {{ title }}
        .col.text-right.pt-2
          i.fas.fa-spinner.fa-spin.fa-4x.copying-property(v-if="copying")
          router-link(to="/" class="btn btn-outline-primary mr-3 px-4 py-0 cancel-save-property") {{ $t('property.cancel') }}
          input.btn.btn-primary.px-4.py-0(
            type='button'
            :value='textSaveButton'
            :disabled="disableSaveButton"
            @click='throttledSubmit'
          )
  .container-fluid.add-container
    .saving-backdrop.text-center(v-if="saving || loading")
      i.fas.fa-spinner.fa-spin.fa-4x
    .container
      .row.pt-4
        form#property-form.col-lg-6(@submit='throttledSubmit', ref='my-form' novalidate)
          .form-group
            .form-check.form-check-inline.mr-4
              label.radio-wrapper.form-check-label(for='radioNew')
                span.form-check-label-text {{ $t('property.new') }}
                input#radioNew.form-check-input(type='radio', name='existing', v-model='property.existing', :value='false')
                span.checkmark
            .form-check.form-check-inline
              label.radio-wrapper.form-check-label(for='radioExisting')
                span.form-check-label-text {{ $t('property.existing') }}
                input#radioExisting.form-check-input(type='radio', name='existing', v-model='property.existing', :value='true')
                span.checkmark
            .invalid-feedback(v-for='obj in errors.existing', role='alert') {{ getError(obj) }}
          .form-group.mb-3.mt-4
            h2 {{ $t('property.basic_information') }}
          .form-group
            label.form-label(for='name') {{ $t('property.property_id') }}
            input#name.form-control(
              placeholder="2020A"
              :class="{ 'red-bordered': !isEmpty(errors.slug) }"
              type='text'
              name='name'
              v-model='property.slug'
              @keyup="allowHalfWidth"
            )
            .invalid-feedback(v-for='obj in errors.slug', role='alert') {{ getError(obj) }}

          .form-group
            label.form-label(for='name') {{ $t('property.name') }} *
            input#name.form-control(
              :placeholder="$t('create_property_page.example_property_name')"
              :class="{ 'red-bordered': !isEmpty(errors.name) }"
              type='text', name='name' v-model='property.name', required)
            .invalid-feedback(v-for='obj in errors.name', role='alert') {{ getError(obj) }}
          .form-row
            .form-group.col-md-6
              label.form-label(for='postCode') {{ $t('property.post_code') }} * {{ $t('half_width') }}
              input#postCode.form-control(
                v-mask="'###-####'"
                :placeholder="$t('create_property_page.example_postal_code')"
                :class="{ 'red-bordered': !isEmpty(errors.post_code) }"
                type='text'
                name='post_code'
                v-model='property.post_code'
                required
              )
              .invalid-feedback(v-for='obj in errors.post_code', role='alert') {{ getError(obj) }}
            .form-group.col-md-6.detect-address-block.pl-3
              a.detect-address(href='#', @click.prevent='detectAddress') {{ $t('property.detect_address') }}
          .form-group
            label.form-label {{ $t('property.address_line1') }} *
            input#addressLine1.form-control(
              :class="{ 'red-bordered': !isEmpty(errors.address_line1) }"
              v-model='property.address_line1'
              name='address_line1'
              :disabled='addressDetected'
              required
            )
            .invalid-feedback(v-for='obj in errors.address_line1', role='alert') {{ getError(obj) }}
          .form-group
            label.form-label {{ $t('property.address_line2') }} {{ $t('half_width') }}
            input#addressLine2.form-control(
              :placeholder="$t('create_property_page.example_address2')"
              :class="{ 'red-bordered': !isEmpty(errors.address_line2) }"
              v-model='property.address_line2'
              name="address_line2"
              @keyup="onInputAddressLine"
            )
          .form-row
            .form-group.col-md-6
              label.form-label {{ $t('property.floors') }} * {{ $t('half_width') }}
              input.form-control(
                :placeholder="$t('create_property_page.example_floors')"
                :class="{ 'red-bordered': !isEmpty(errors.floors) }"
                type='number'
                name='floors'
                v-model='property.floors'
                min='1'
                step='1'
                required
                @keyup='asNumber($event)'
              )
              .invalid-feedback(v-for='obj in errors.floors', role='alert') {{ getError(obj) }}
            .form-group.col-md-6
              label.form-label {{ $t('property.area') }} * {{ $t('half_width') }}
              input#number.form-control(
                :placeholder="$t('create_property_page.example_area')"
                :class="{ 'red-bordered': !isEmpty(errors.area) }"
                type='number'
                name='area'
                v-model='property.area'
                required
                min='0'
                max='99000000'
                step='.01'
                @keyup='asNumber($event)'
              )
              .invalid-feedback(v-for='obj in errors.area', role='alert') {{ getError(obj) }}
          .form-row
            .form-group.col-md-6(v-if='property.existing')
              label.form-label(for='ex1') {{ $t('property.built_date') }} *
              date-picker(
                :input-class="['form-control', 'readonly', {'red-bordered': !isEmpty(errors.built_date)}]"
                :input-attr="{ required: true }"
                name='built_date'
                :placeholder="$t('create_property_page.example_date')"
                :editable='false'
                v-model='property.built_date'
                :format='datePicker.format'
                :not-before="datePicker.disabledDates.built_date.before"
                :not-after="datePicker.disabledDates.built_date.after"
                :lang="datePicker.lang"
                :value-type="datePicker.internalFormat"
              )
              .invalid-feedback(v-for='obj in errors.built_date', role='alert') {{ getError(obj) }}
            .form-group.col-md-6
              label.form-label(for='ex1') {{ $t('property.time_nearest_station') }} *
              input#number.form-control(
                :placeholder="$t('create_property_page.example_nearest_station')"
                :class="{ 'red-bordered': !isEmpty(errors.time_nearest_station) }"
                type='number'
                name='time_nearest_station'
                v-model='property.time_nearest_station'
                required
                min='1'
                step='1'
                @keypress='filterDigits'
                @keyup='asNumber($event)'
              )
              .invalid-feedback(v-for='obj in errors.time_nearest_station', role='alert') {{ getError(obj) }}
          .form-row
            .form-group.col-md-6
              label.form-label {{ $t('property.building_structure') }} *
              select.form-control.select-wraper(
                :class="{ 'red-bordered': !isEmpty(errors.building_structure) }"
                name='building_structure', v-model='property.building_structure', required)
                option(value='src') {{ $t('building_structure.src') }}
                option(value='rc') {{ $t('building_structure.rc') }}
                option(value='s') {{ $t('building_structure.s') }}
                option(value='w') {{ $t('building_structure.w') }}
                option(value='others') {{ $t('building_structure.others') }}
              i.fa.fa-chevron-down.icon-action
              .invalid-feedback(v-for='obj in errors.building_structure', role='alert') {{ getError(obj) }}
          .form-row
            .form-group.col-md-12(v-if='property.existing')
              .row
                .col-12
                  label.form-label {{ $t('property.renovation_date') }}

              .row
                .col-3.pt-2 {{ $t('property.renovation_exterior') }}
                .col-2.pt-1
                  label.switch
                    input(type="checkbox" v-model="toggles.exterior" @change="toggleDate($event, 'renovation_date_exterior')")
                    span.slider.round
                .col-7(v-if="toggles.exterior")
                  date-picker(
                    input-class="form-control readonly"
                    :input-attr="{ required: true }"
                    name='renovation_date_exterior'
                    :placeholder="$t('create_property_page.example_date')"
                    :editable='false'
                    v-model='property.renovation_date_exterior'
                    :format='datePicker.format'
                    :not-before="datePicker.disabledDates.renovation_date.before"
                    :not-after="datePicker.disabledDates.renovation_date.after"
                    :lang="datePicker.lang"
                    :value-type="datePicker.internalFormat"
                  )
              .row.mb-3
                .col-12
                  .invalid-feedback(v-for='obj in errors.renovation_date_exterior', role='alert') {{ getError(obj) }}

              .row
                .col-3.pt-2 {{ $t('property.renovation_common') }}
                .col-2.pt-1
                  label.switch
                    input(type="checkbox" v-model="toggles.common" @change="toggleDate($event, 'renovation_date_common')")
                    span.slider.round
                .col-7(v-if="toggles.common")
                  date-picker(
                    input-class="form-control readonly"
                    :input-attr="{ required: true }"
                    name='renovation_date_exterior'
                    :placeholder="$t('create_property_page.example_date')"
                    :editable='false'
                    v-model='property.renovation_date_common'
                    :format='datePicker.format'
                    :not-before="datePicker.disabledDates.renovation_date.before"
                    :not-after="datePicker.disabledDates.renovation_date.after"
                    :lang="datePicker.lang"
                    :value-type="datePicker.internalFormat"
                  )
              .row
                .col-12
                  .invalid-feedback(v-for='obj in errors.renovation_date_common', role='alert') {{ getError(obj) }}

          .form-group.mb-4.mt-3
            a.add-facilities-link(v-b-modal.list-facilities, href='#', @click.prevent='storeUnsavedFacilities') {{ $t('property.add_facilities') }}

          image-uploader.mb-5(
            v-model="property.images"
            :external-errors="errors.images"
          )

      .row.pt-3
        .col-lg-4.col-12.rooms-headline
          .form-group
            h2 {{ $t('property.room_items') }}
        .col-lg-8.col-12.text-lg-right.text-left
          .form-group
            b-button.btn.btn-outline-primary.px-4.py-0.mr-1(@click="downloadExampleXlsx") {{ $t('property.download_excel_template') }}
            b-button.btn.btn-outline-primary.px-4.py-0.mr-1(@click="uploadRooms") {{ $t('property.upload_rooms_list') }}
            b-button.btn.btn-primary.px-4.py-0(v-b-modal.modal-add-edit-room, @click='addRoom')
              i.fas.fa-plus.mr-2
              span {{ $t('property.add_room') }}

      .row(v-if="size(uploadErrors)")
        .col
          b-alert(show variant="danger")
            ul#upload-errors-list
              li(v-for="error in uploadErrors")
                span {{ $t('errors.rooms_import_error', error) }}
      .row
        .col
          table.table
            thead
              tr
                th.form-label(scope='col') {{ $t('room.number') }}
                th.form-label(scope='col') {{ $t('room.floor') }}
                th.form-label(scope='col') {{ $t('room.layout') }}
                th.form-label(scope='col') {{ $t('room.area') }}
                th.form-label(scope='col') {{ $t('room.current_rent_fee') }}
                th.form-label(scope='col')
                  div {{ $t('room.renovation_interior1') }}
                  div {{ $t('room.renovation_interior2') }}
                th.form-label(scope='col')
                  div {{ $t('room.renovation_wet1') }}
                  div {{ $t('room.renovation_wet2') }}
                th.form-label(scope='col') {{ $t('room.direction') }}
                th.form-label(scope='col')
                th.form-label(scope='col')
                th.form-label(scope='col')
            tbody
              template(v-for='(room, index) in rooms')
                room-details(
                  :class="{ 'destroyed-rooms': room._destroy }"
                  :room='room'
                  :isDestroyed='room._destroy'
                  @onRoomDeleteReturn='setCurrentRoomAndDeleteReturn(room, index)'
                  @onShowFacilities='setCurrentRoom(room, index)'
                  @onRoomEdit='editRoom(room, index)'
                  @confirmDeleteRoom="confirmDeleteRoom(index)"
                )
              tr
                td
                td {{ $t('property.total_area') }}
                td.total-area {{ $n(Math.round(totalArea * 100) / 100) }}
                td(colspan='8')

      b-modal#list-facilities.modal-background.modal-wide(:title="$t('property.list_facilities')", size='lg', hide-header hide-footer no-close-on-backdrop no-close-on-esc)
        .container
          .row.mb-5.mt-3
            .col
              h2 {{ $t('property.list_facilities') }}
          .row
            .col-md-4.col-sm-6(v-for='facility in propertyFacilities', :key='facility.id')
              ul.pl-2
                li
                  label.checkbox-wrapper {{ $t(`facilities.${facility.code}`) }}
                    input(type='checkbox', :value='facility.id', v-model='property.facility_ids')
                    span.checkmark
          .row.mt-2
            .col.text-right
              button.btn.btn-outline-primary.mr-3.py-0.px-4(@click.prevent="closeModal('list-facilities')") {{ $t('property.cancel') }}
              button.btn.btn-primary.py-0.mr-1(@click="$bvModal.hide('list-facilities')") {{ $t('confirm') }}

      b-modal#modal-room-facilities.modal-background(hide-header hide-footer no-close-on-backdrop)
        .row.mb-4.mt-3
          .col.text-center
            p {{ $t('property.list_facilities') }}
        .row(v-if='currentRoom.facility_ids.length > 0')
          ul
            li(v-for='facility in facilities', :key='facility.id', v-if='currentRoom.facility_ids.includes(facility.id)')
              span {{ $t(`facilities.${facility.code}`) }}
        .row(v-else)
          .col.text-center
            p.text {{ $t('property_page.no_facilities') }}
        .row.mt-3
          .col.text-center
            button.btn.btn-primary.py-0.mr-1.px-4(@click="$bvModal.hide('modal-room-facilities')") {{ $t('property.ok') }}

      b-modal#modal-info.modal-background(hide-header hide-footer no-close-on-backdrop)
        .row.mb-4.mt-3
          .col.text-center
            p {{ modalInfoText }}
        .row
          .col.text-center
            button.btn.btn-primary.py-0.mr-1.px-4(@click="$bvModal.hide('modal-info')") {{ $t('property.ok') }}

    add-room(
      :facilities='facilities'
      :property='property'
      :room='currentRoom'
      :add='currentRoomIndex == null'
      :rooms='roomsExceptCurrent'
      @onRoomChanged="onRoomChanged"
    )

  b-modal#modal-popup(
    v-model="modals.uploadRooms.shown"
    hide-header
    hide-footer
    no-close-on-backdrop
  )
    upload-rooms-modal(
      v-if="modals.uploadRooms.shown"
      :propertyId="property.id"
      @success="onUploadSuccess"
      @error="onUploadError"
      @close="modals.uploadRooms.shown = false"
    )

  b-modal#copy-property-popover(hide-header hide-footer)
    .copy-message
      p {{ $t('copy_property.start_to_copy') }}
      router-link(to='/') {{ $t('copy_property.to_property_list') }}

</template>

<script>
import { api } from '@/api'
import RoomDetails from '@/pages/Property/Add/room-details.vue'
import AddRoom from '@/pages/Property/Add/add-room.vue'
import UploadRoomsModal from './modals/upload-rooms'
import DatePicker from 'vue2-datepicker'
import fecha from 'fecha'
import { isEmpty, size, map, omit, find, cloneDeep, some, filter, uniqBy, maxBy, get, throttle, isUndefined } from 'lodash'
import errorMessageMixin from '@/mixins/error-message'
import ImageUploader from '@/pages/Property/Add/image-uploader.vue'
import objectToFormData from 'object-to-formdata'
import { mask } from 'vue-the-mask'
import currentUserMixin from '@/mixins/current-user'
import { mapState } from 'vuex'
import { CUSTOM_POSTAL_CODES } from "@/constants.js"

export default {
  directives: {
    mask
  },
  components: {
    DatePicker,
    RoomDetails,
    AddRoom,
    UploadRoomsModal,
    ImageUploader
  },

  mixins: [
    errorMessageMixin,
    currentUserMixin
  ],

  data() {
    return {
      modals: {
        uploadRooms: {
          shown: false
        }
      },
      addressDetected: false,
      uploadErrors: [],
      errors: {},
      facilities: [],
      geocoder: new google.maps.Geocoder(),
      property: {
        existing: false,
        facility_ids: []
      },
      rooms: [],
      currentRoom: {
        facility_ids: []
      },
      currentRoomIndex: null,
      unSavedFacilityIds: [],
      dataChanged: false,
      modalInfoText: this.$t('property.changes_saved'),
      datePicker: {
        disabledDates: {
          renovation_date: {
            before: this.defaultBeforeBorderDate,
            after: this.defaultAfterBorderDate
          },
          built_date: {
            before: this.defaultBeforeBorderDate,
            after: this.defaultAfterBorderDate
          }
        },
        format: 'YYYY年M月D日',
        internalFormat: {
          value2date(value) {
            if (!value) {
              return null
            }
            return new Date(value)
          },
          date2value(date) {
            if (!date) {
              return
            }

            return fecha.format(date, 'YYYY-MM-DD')
          }
        },
        lang: {
          days: ['日', '月', '火', '水', '木', '金', '土'],
          months: ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月']
        }
      },
      dialogOptions: {
        cancelText: this.$t('room.cancel'),
        okText: this.$t('property.ok')
      },
      watcherEnabled: false,
      saving: false,
      copying: false,
      loading: false,
      toggles: {
        exterior: false,
        common: false
      },
      disableSaveButton: false,
      existsIds: []
    }
  },

  computed: {
    ...mapState(['currentUser']),

    isPropertyCreated() {
      return (this.$route.params.id != undefined && !this.copyProperty)
    },
    textSaveButton() {
      if (this.isPropertyCreated) {
        return this.$t('property.save_changes')
      } else {
        return this.$t('property.add_property')
      }
    },
    title() {
      if (!this.$route.params.id) {
        return this.$t('property.add_property')
      } else if (this.copyProperty) {
        return this.$t('property.copy_property')
      } else {
        return this.$t('property.edit_property')
      }
    },
    totalArea() {
      return this.notDeletedRooms.reduce((accumulator, room) => {
        return accumulator + parseFloat(room.area)
      }, 0)
    },
    notDeletedRooms() {
      return filter(this.rooms, room => !room._destroy)
    },
    roomsExceptCurrent() {
      return filter(this.rooms, (_room, index) => index != this.currentRoomIndex)
    },
    fullAddress() {
      return this.property.address_line1 + ' ' + this.property.address_line2
    },

    copyProperty() {
      return this.$route.name == 'PropertyCopy'
    },

    defaultBeforeBorderDate () {
      return this.property.built_date || new Date('1000-01-01')
    },

    defaultAfterBorderDate () {
      return new Date()
    },

    propertyFacilities() {
      return this.facilities.filter((facility) => {
        return facility.property_facility
      })
    },

    throttledSubmit() {
      return throttle(this.onSubmit, 2000)
    },

    loadExistsSlugsUrl() {
      return 'properties/exists_slugs'
    }
  },

  watch: {
    rooms() {
      if (!this.watcherEnabled) {
        return
      }

      this.dataChanged = true
    },
    ['property.built_date']() {
      this.datePicker.disabledDates.renovation_date.before = new Date(this.property.built_date)
    },
    property: {
      deep: true,
      handler() {
        if (!this.watcherEnabled) {
          return
        }
        this.dataChanged = true
        window.onbeforeunload = () => ''
      },
    }
  },

  created() {
    this.loadingForCopy()
    if (this.$route.params.id) {
      api.get(`properties/${this.$route.params.id}`).then(({ data: { data } }) => {
        this.property = data
        this.deleteRoomsIdsForCopy()
        this.rooms = this.property.rooms

        this.toggles.exterior = !!this.property.renovation_date_exterior
        this.toggles.common = !!this.property.renovation_date_common
        this.loading = false
        this.$nextTick(() => {
          this.watcherEnabled = true
        })
      }).then(() => {
        this.loadExistsSlugs()
      })
    } else {
      this.$nextTick(() => {
        this.watcherEnabled = true
      })
      this.loadExistsSlugs()
    }
  },

  mounted() {
    this.checkUserReadOnly()
    api.get('/facilities').then((response) => {
      this.facilities = response.data.data
    })
  },

  destroyed() {
    window.onbeforeunload = null
  },

  beforeRouteLeave (to, from, next) {
    if (this.dataChanged) {
      this.$dialog.confirm(this.$t('property.go_to_another_page_message'), this.dialogOptions).then(() => {
        next()
      }).catch(() => {
        next(false)
      })
    } else {
      next()
    }
  },

  methods: {
    isEmpty,
    size,
    map,
    find,

    downloadExampleXlsx() {
      const url = '/examples/rooms.xlsx'
      window.open(url, '_blank')
    },

    uploadRooms() {
      this.modals.uploadRooms.shown = true
    },

    onUploadSuccess(newRooms) {
      this.rooms = [ ...this.rooms, ...newRooms ]
      this.uploadErrors = []
    },

    onUploadError(errors) {
      this.uploadErrors = errors
    },

    onSubmit() {
      this.disableSaveButton = true
      if (this.validate()) {
        if (this.needConfirmation()) {
          this.disableSaveButton = false
          this.$dialog.confirm(this.$t('property.clear_fees_message'), this.dialogOptions).then(() => {
            this.save()
          })
        } else {
          this.save()
        }
      } else {
        this.disableSaveButton = false
      }
    },

    needConfirmation() {
      let hasFeesOrDates = some(this.notDeletedRooms, room => {
        return room.current_rent_fee || room.renovation_date_interior || room.renovation_date_wet
      })
      return !this.property.existing && hasFeesOrDates
    },

    async save() {
      let { lat, lon } = await this.detectCoordinates()
      this.property.lat = lat
      this.property.lon = lon

      this.clearDates()
      this.clearRoomFeesAndDates()

      this.property.images_attributes = map(this.property.images, image => omit(image, 'url'))
      this.property.rooms_attributes = this.rooms

      let propertyData = this.property
      propertyData.copy = false

      if (this.copyProperty) {
        propertyData.id = null
        propertyData.images_attributes = this.imagesAttributes()
        propertyData.copy = true
      }

      let formData = objectToFormData(omit(propertyData, ['images', 'rooms']), { indices: true })
      let request = this.makeRequest(formData)

      request.then(() => {
        this.dataChanged = false

        if (!this.copyProperty) {
          this.$router.push('/properties', () => {
            this.$bvToast.show('saved-notification')
          })
        }
      }).catch(error => {
        this.errors = error.response.data.errors
      }).finally(() => {
        this.saving = false
        this.copying = false
      })
    },

    imagesAttributes() {
      return this.property.images.map(({ url, file }) => {
        if (file) {
          return { file }
        } else {
          return { remote_file_url: process.env.VUE_APP_BASE_PATH + url }
        }
      })
    },

    makeRequest(formData) {
      const config = {
        headers: { 'Content-Type': 'multipart/form-data' }
      }

      if (!this.copyProperty) {
        this.saving = true
      } else {
        this.copying = true
        this.$bvModal.show('copy-property-popover')
      }

      if (this.isPropertyCreated) {
        return api.put(`/properties/${this.$route.params.id}`, formData, config)
      } else {
        return api.post('/properties', formData, config)
      }
    },

    validate() {
      this.errors = {}

      this.validateForm()
      this.validateAddress()
      this.validateDates()
      this.validateArea()
      this.validateFloors()
      this.validateRoomNumbers()
      this.validateId()

      return (size(this.errors) == 0)
    },

    validateForm() {
      const nodes = document.querySelectorAll(`#property-form :invalid`)

      if (nodes.length > 0) {
        nodes.forEach(node => {
          this.errors = { ...this.errors, [node.name]: [{ error: node.validationMessage }] }
        })
      }
    },

    validateAddress() {
      if (!this.addressDetected && isEmpty(this.errors.post_code)) {
        this.setPostCodeError(this.$t('errors.pls_detect_address'))
      }
    },

    validateDates() {
      if (!this.property.built_date && this.property.existing) {
        this.errors.built_date = [{ error: 'fill_out_this_field' }]
      }

      if (this.property.built_date && this.property.renovation_date_exterior) {
        if (new Date(this.property.built_date).getTime() > new Date(this.property.renovation_date_exterior).getTime()) {
          this.errors.renovation_date_exterior = [{ error: 'renovation_date_should_be_greater_built_date' }]
        }
      }

      if (this.property.built_date && this.property.renovation_date_common) {
        if (new Date(this.property.built_date).getTime() > new Date(this.property.renovation_date_common).getTime()) {
          this.errors.renovation_date_common = [{ error: 'renovation_date_should_be_greater_built_date' }]
        }
      }

      if (this.property.existing && this.property.built_date) {
        let built_date = new Date(this.property.built_date).getTime()
        let invalidRenovationDates = some(this.notDeletedRooms, room => {
          let invalidInterior = (room.renovation_date_interior && (new Date(room.renovation_date_interior).getTime() < built_date))
          let invalidWet = (room.renovation_date_wet && (new Date(room.renovation_date_wet).getTime() < built_date))
          return (invalidInterior || invalidWet)
        })

        if (invalidRenovationDates) {
          this.errors.built_date = [{ error: 'built_date_should_be_earlier_room_renovation_date' }]
        }

        let futureRenovationDates = some(this.notDeletedRooms, room => {
          let futureInterior = (room.renovation_date_interior && (new Date(room.renovation_date_interior).getTime() > new Date().getTime()))
          let futureWet = (room.renovation_date_wet && (new Date(room.renovation_date_wet).getTime() > new Date().getTime()))
          return (futureInterior || futureWet)
        })

        if (futureRenovationDates) {
          this.errors.existing = [{ error: 'room_renovation_date_in_future' }]
        }
      }
    },
    validateArea() {
      if (this.totalArea > this.property.area && !this.errors.area) {
        this.errors.area = [{ error: 'total_sum_greater_then_property_area' }]
      }
    },
    validateFloors() {
      if (some(this.notDeletedRooms, room => parseInt(room.floor) > parseInt(this.property.floors))) {
        this.errors.floors = [{ error: 'property_floors_must_be_greater' }]
      }
    },

    validateRoomNumbers() {
      const notPresent = some(this.rooms, room => !room.number)
      if (this.property.existing && notPresent) {
        this.errors.existing = [{ error: 'all_rooms_must_have_numbers' }]
      }

      const notPositive = some(this.notDeletedRooms, room => {
        return parseInt(room.number) <= 0
      })
      if (notPositive) {
        this.errors.existing = [{ error: 'room_number_must_be_positive' }]
      }

      const roomsWithNumber = filter(this.notDeletedRooms, 'number')
      const notUnique = (roomsWithNumber.length != uniqBy(roomsWithNumber, 'number').length)
      if (notUnique) {
        this.errors.existing = [{ error: 'room_number_must_be_unique' }]
      }
    },

    validateId() {
      if (this.existsIds.includes(this.property.slug)) {
        this.errors.slug = [{ error: 'such_id_already_exists' }]
      }
    },

    detectAddress() {
      if (this.property.post_code) {
        this.deleteAllPostCodeErrors()

        this.handleCustomPostalCode()
        if (this.addressDetected) {
          return
        }

        this.geocoder.geocode({
          componentRestrictions: { postalCode: this.property.post_code, country: 'JP' }
        }, (results, status) => {
          if (status == 'OK') {
            this.setAddressByLocation(results[0].geometry.location)
          } else {
            this.setPostCodeError(this.$t('errors.can_not_detect_addrees'))
            this.property.address_line1 = ''
            this.addressDetected = false
          }
        })
      }
    },

    deleteAllPostCodeErrors() {
      delete this.errors.post_code
    },

    handleCustomPostalCode() {
      const address = CUSTOM_POSTAL_CODES[this.property.post_code]
      if (isUndefined(address)) {
        return
      }

      this.$set(this.property, 'address_line1', address)
      this.addressDetected = true
    },

    detectCoordinates() {
      return new Promise(resolve => {
        this.geocoder.geocode({
          address: this.fullAddress,
          componentRestrictions: { country: 'JP' }
        }, (results, status) => {
          if (status == 'OK') {
            let location = results[0].geometry.location

            resolve({ lat: location.lat(), lon: location.lng() })
          } else {
            resolve({ lat: this.property.lat, lon: this.property.lon })
          }
        })
      })
    },

    setAddressByLocation(location) {
      this.property.lat = location.lat()
      this.property.lon = location.lng()

      this.geocoder.geocode({ latLng: location }, (results, status) => {
        if (status == 'OK') {
          const components = maxBy(results, item => item.address_components.length).address_components
          this.$set(this.property, 'address_line1', this.addressString(components))
          this.addressDetected = true
        } else {
          this.setPostCodeError(this.$t('errors.can_not_detect_addrees'))
          this.property.address_line1 = ''
          this.addressDetected = false
        }
      })
    },

    addressString(components) {
      const town = this.addressComponent(components, ['sublocality_level_2'])
      const city = this.addressComponent(components, ['political', 'locality']) +
                     this.addressComponent(components, ['sublocality_level_1'])
      const prefecture = this.addressComponent(components, ['administrative_area_level_1'])
      const stations = this.addressComponent(components,
          ['establishment', 'train_station', 'transit_station', 'point_of_interest'])

      return [prefecture, city, town, stations].join(' ')
    },

    addressComponent(components, keys) {
      const component = components.find(({ types }) => {
        return keys.reduce((acc, key) => acc && types.includes(key), true)
      })

      return get(component, 'long_name') || ''
    },

    setPostCodeError(error) {
      this.errors = { ...this.errors, post_code: [{ error }] }
    },

    addRoom() {
      this.currentRoom = {
        layout: '',
        floor: '',
        area: '',
        renovation_date_interior: '',
        renovation_date_wet: '',
        direction: '',
        current_rent_fee: '',
        facility_ids: []
      }
      this.currentRoomIndex = null
    },
    editRoom(room, index) {
      this.currentRoom = cloneDeep(room)
      this.currentRoomIndex = index
    },
    onRoomChanged(room) {
      if (this.currentRoomIndex != null) {
        this.$set(this.rooms, this.currentRoomIndex, room)
      } else {
        this.rooms.push(room)
      }
    },

    setCurrentRoomAndDeleteReturn(room, index) {
      this.setCurrentRoom(room, index)
      this.deleteReturnRoom()
    },

    setCurrentRoom(room, index) {
      this.currentRoom = room
      this.currentRoomIndex = index
    },

    deleteReturnRoom() {
      let room = this.rooms[this.currentRoomIndex]
      room._destroy = !room._destroy

      this.$set(this.rooms, this.currentRoomIndex, room)
      this.$bvModal.hide('modal-delete-return-room')
    },
    clearDates() {
      if (!this.property.existing) {
        this.property.built_date = ''
        this.property.renovation_date_exterior = ''
        this.property.renovation_date_common = ''
      }
    },
    clearRoomFeesAndDates() {
      if (!this.property.existing) {
        this.rooms.forEach(room => {
          room.current_rent_fee = null
          room.renovation_date_interior = null
          room.renovation_date_wet = null
        })
      }
    },

    asNumber({ target: { value }}) {
      this.property = { ...this.property, target: Number(value) }
    },

    storeUnsavedFacilities() {
      this.unSavedFacilityIds = this.property.facility_ids
    },
    closeModal(id) {
      this.property.facility_ids = this.unSavedFacilityIds
      this.$bvModal.hide(id)
    },
    toggleDate(event, date) {
      if (!event.target.checked) {
        this.property[date] = ''
      }
    },
    onInputAddressLine({ isTrusted, target: { value }}) {
      if (!isTrusted) {
        return
      }
      this.property.address_line2 = value.replace(/[^\u0030-\u0039\u002D]/g, '')
    },

    checkUserReadOnly() {
      if (this.isUserReadOnly) {
        this.$router.push({ name: "PropertyList" })
      }
    },

    loadingForCopy() {
      if (this.copyProperty) {
        this.loading = true
      }
    },

    filterDigits(event) {
      if (event.charCode < 48 || event.charCode > 57) {
        event.preventDefault()
      }
    },

    destroyedRoom(room) {
      return room._destroy == false || isEmpty(room._destroy)
    },

    loadExistsSlugs() {
      const params = this.loadExistsSlugsParams()

      api.get(this.loadExistsSlugsUrl, { params }).then(({ data: { data } }) => {
        this.existsIds = data
      })
    },

    loadExistsSlugsParams() {
      if (this.copyProperty) {
        return {}
      } else {
        return { slug: this.property.slug}
      }
    },

    allowHalfWidth({ target: { value } }) {
      const replacedValue = filter(value.split(''), (symbol) => symbol.match(/[a-z0-9A-z]/)).join('')
      this.property = { ...this.property, slug: replacedValue }
    },

    confirmDeleteRoom(index) {
      this.$delete(this.rooms, index)
    },

    deleteRoomsIdsForCopy() {
      if (this.copyProperty) {
        this.property.rooms.forEach((el) => el.id = null)
      } else {
        return null
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.saving-backdrop {
  background-color: rgba(200, 200, 200, 0.6);
  bottom: 0;
  left: 0;
  padding-top: 20vh;
  position: absolute;
  right: 0;
  top: -10px;
  z-index: 10;
}

.property-add-header {
  background-color: #fff;
  box-shadow: 0 2px 16px 0 rgba(176, 186, 197, 0.2);
  margin-bottom: 10px;
}

#upload-errors-list {
  margin: 16px 0;
}

h3 {
  color: #7c91a7;
  font-size: 11px;
  font-weight: 700;
}

h1 {
  color: #3d4a57;
  font-size: 30px;
  font-weight: 700;
}

h2 {
  color: #3d4a57;
  font-size: 22px;
  font-weight: 700;
  letter-spacing: 0;
}

.btn-primary,
.btn-primary:hover,
.btn-primary:focus,
.btn-primary:active {
  background-image: linear-gradient(90deg, rgba(24, 172, 235, 0.98) 0%, #18a4eb 100%);
  border-color: transparent !important;
  border-radius: 4px;
  box-shadow: none;
  font-size: 14px;
  font-weight: 500;
  height: 40px;
  line-height: 38px;
}

.btn-outline-primary,
.btn-outline-primary:hover,
.btn-outline-primary:focus,
.btn-outline-primary:active {
  background: transparent;
  border: 1px solid rgba(0, 156, 234, 0.3);
  border-radius: 4px;
  color: #009cea;
  font-size: 14px;
  height: 40px;
  line-height: 38px;
}

.modal-background {
  ::v-deep .modal-content {
    background-color: #fcfdfe !important;
  }

  ul {
    list-style-type: none;
    margin-left: -5px;
  }
}

.modal-wide {
  ::v-deep .modal-dialog {
    max-width: 850px;
    width: 850px;
  }
}

.add-container {
  background: #fcfdfe;
  position: relative;

  .rooms-headline {
    align-items: center;
    display: flex;
  }

  .form-check-label-text {
    color: #3d4a57;
    font-size: 16px;
    font-weight: 500;
    letter-spacing: 0;
  }

  ::v-deep .form-label {
    color: #7c91a7;
    font-size: 13px;
    font-weight: 700;
  }

  .form-control {
    border: 1px solid #dfe6ee;
    border-radius: 4px;
    color: #313541;
    font-size: 14px;
    font-weight: 500;
    height: 40px;
    padding: 10px 12px;
  }

  input.form-control {
    line-height: 38px;
  }

  .invalid-feedback {
    color: #dc3545;
    display: block;
    font-size: 100%;
    margin-top: 0.25rem;
    width: 100%;
  }

  a {
    color: #008bd0;
  }

  table {
    border: 1px solid #dee2e6;

    th,
    td {
      border: none;
    }

    th {
      color: #7c91a7;
      font-size: 12px !important;
      font-weight: 700;
      letter-spacing: 0;
    }

    td {
      border-top: 1px solid #e7ecf4;
      color: #313541;
      font-size: 13px;
      letter-spacing: 0;
      vertical-align: middle;

      &.total-area {
        font-size: 18px;
        font-weight: 700;
      }
    }
  }

  .detect-address-block {
    padding-top: 31px;

    .detect-address {
      font-size: 14px;
      font-weight: 500;
      letter-spacing: 0;
      line-height: 40px;

      &:hover {
        text-decoration: none;
      }
    }
  }

  .add-facilities-link {
    font-size: 14px;
    font-weight: 500;
    letter-spacing: 0;

    &:hover {
      text-decoration: none;
    }
  }

  .icon-action {
    color: #7c91a7;
    font-size: 16px;

    &:hover {
      color: #009cea;
    }
  }

  .text {
    color: #313541;
    font-size: 14px;
  }
}

.title-path {
  a,
  span {
    color: #7c91a7;
    font-size: 11px;
    letter-spacing: 0;
  }

  a:hover {
    color: #7c91a7;
    text-decoration: none;
  }

  #arrow-delimiter {
    margin: 0 5px;
  }
}

.table {
  thead {
    th {
      vertical-align: middle;
    }
  }
}

input::placeholder {
  opacity: 0.5;
}

::v-deep .mx-datepicker {
  width: 100%;

  input {
    font-family: 'Noto Sans JP', sans-serif !important;
    font-size: 14px !important;
    font-weight: 500;

    &::placeholder {
      opacity: 0.5;
    }
  }
}

.copying-property {
  font-size: 30px;
  margin-right: 40px;
}

@media (max-width: 768px) {
  .detect-address-block {
    padding: 0 0 0 8px !important;
  }
}
</style>
